import { Injectable, inject } from '@angular/core';
import { ExpenditureEntryService } from '../../project-edit/manage-project/project-calculation-form/services/expenditure-entry.service';
import { ProjectModel } from '../../../../../core/models/project/project.model';
import { TimeBlockWithTask } from '../../../../../shared/data-types/shared-time-block-types';
import { Interval } from 'date-fns';
import { TimeBlockFee } from '../../project-edit/edit-project.types';
import { TimeBlockDurationCalculationService } from '../../../../../features/calendar/components/time-block/calculation/duration/time-block-duration-calculation.service';
import { DateTimeHelper } from '../../../../../features/calendar/util/date-time-helper';
import { DefaultDateFormat } from '../../../../../shared/localization/formats';
import { ITimeBlockComponentItem } from '../../../../../features/calendar/components/time-block/types/time-block-component-item-types';
import { ProjectExpenditureEntryModel } from '../../../../../core/models/project/project-expenditure-entry.model';
import { pairwise } from '../../../../../shared/functions/array-functions';
import { ProjectMonetaryCalculationService } from './project-monetary-calculation.service';

@Injectable({
  providedIn: 'root',
})
export class TimeBlockFeeCalculationService {
  private readonly expenditureEntryService = inject(ExpenditureEntryService);
  private readonly timeBlockDurationCalculationService = inject(
    TimeBlockDurationCalculationService,
  );
  private readonly projectMonetaryCalculationService = inject(ProjectMonetaryCalculationService);

  public timeBlockFeesOfProject(
    project: ProjectModel,
    timeBlocksWithTask: readonly TimeBlockWithTask[],
    excludeOverlappingTime: boolean,
    timeRange: Interval = {
      start: null,
      end: null,
    },
  ): TimeBlockFee[] {
    const timeBlockComponents = timeBlocksWithTask.map((tb) => tb.timeBlock);
    const splitTimeBlockMapPerDay =
      this.timeBlockDurationCalculationService.createTimeBlockMapPerDay(
        timeBlockComponents,
        excludeOverlappingTime,
        timeRange,
      );

    const timeBlockFees: TimeBlockFee[] = [];
    for (const [dateString, timeBlocks] of splitTimeBlockMapPerDay) {
      for (const timeBlock of timeBlocks) {
        const date = DateTimeHelper.parse(dateString, DefaultDateFormat, new Date());
        const projectExpenditureEntryModel = this.getFeeOnDateForTimeBlock(
          timeBlock,
          project,
          date,
        );
        const timeBlockFee: TimeBlockFee = {
          timeBlockWithTask: {
            timeBlock,
            task: timeBlocksWithTask.find((tb) => tb.timeBlock.id === timeBlock.id)!.task,
          },
          hourlyRate: null,
        };
        // if project.expenditureType !== ExpenditureRateOptions.ExpendituresInHours
        if (projectExpenditureEntryModel !== null) {
          timeBlockFee.hourlyRate = projectExpenditureEntryModel.expenditureValue;
        }
        timeBlockFees.push(timeBlockFee);
      }
    }
    return timeBlockFees;
  }

  private getFeeOnDateForTimeBlock(
    timeBlock: ITimeBlockComponentItem,
    project: ProjectModel,
    date: Date,
  ): ProjectExpenditureEntryModel | null {
    const expenditureEntries = [
      ...this.projectMonetaryCalculationService.getExpenditureEntriesForTimeBlock(
        timeBlock,
        project,
      ),
    ];

    if (expenditureEntries.length === 0) {
      return null;
    }
    // add dummy entry at beginning and the end
    expenditureEntries.push(
      this.expenditureEntryService.createExpenditureEntryModel(
        expenditureEntries[0].expenditureValue,
        undefined,
        undefined,
        undefined,
      ),
    );
    expenditureEntries.unshift(
      this.expenditureEntryService.createExpenditureEntryModel(
        expenditureEntries[expenditureEntries.length - 1].expenditureValue,
        undefined,
        undefined,
        undefined,
      ),
    );

    const intervals = pairwise<boolean>(
      expenditureEntries,
      (first: ProjectExpenditureEntryModel, second: ProjectExpenditureEntryModel) => {
        return DateTimeHelper.isDateInOpenInterval(date, {
          start: first.startingFrom ?? null,
          end: second.startingFrom ?? null,
        });
      },
    );

    const entryIndex = intervals.indexOf(true) === 0 ? 1 : intervals.lastIndexOf(true);
    return expenditureEntries[entryIndex];
  }
}
